/*
 * Device-binding banner + selfie unlock dialog for field supervisors.
 *
 * Rendered inside <AppShell> for both PMC and Contractor supervisors. It
 * watches the cached `deviceStatus` from the auth context and:
 *   • When status is "approved" or "not_required" → renders nothing.
 *   • When status is "pending" → renders a sticky amber banner explaining
 *     that the device is awaiting admin approval and (if a selfie has
 *     not yet been submitted) a "Submit selfie for approval" button that
 *     opens the capture dialog.
 *   • When status is "revoked" → renders a red banner with the same
 *     selfie button so the supervisor can request re-approval.
 *
 * The supervisor can still navigate, see data, and prepare captures
 * locally; only writes (POST /attendance, mark-absent, hourly-selfie)
 * are blocked by the backend until approval. Those failing calls return
 * HTTP 423 and our axios interceptor refreshes `deviceStatus` right
 * away. AuthProvider also polls GET /me/devices every 20s (see
 * lib/auth.jsx) so this banner clears automatically once an admin
 * approves the device, even without triggering a write or reloading.
 */
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, deviceStatusStore } from "@/lib/api";
import { getDeviceId, getFingerprint } from "@/lib/device";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldAlert, Camera, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { errorMsg } from "@/lib/utils-app";

export function DeviceStatusBanner() {
  const { user, deviceStatus, setDeviceStatus } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selfie, setSelfie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Only field supervisors are device-bound; admins are exempt.
  const isField = user && (user.role === "pmc_supervisor" || user.role === "contractor_supervisor" || user.role === "supervisor_incharge");
  const autoPromptedRef = useRef(false);

  useEffect(() => {
    if (isField && (deviceStatus === "pending" || deviceStatus === "revoked") && !autoPromptedRef.current) {
      autoPromptedRef.current = true;
      openDialog();
    }
  }, [isField, deviceStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => stopCamera(), []);

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  async function startCamera() {
    stopCamera();
    setSelfie(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      toast.error("Camera access denied. Please allow camera permission.");
    }
  }

  function capture() {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 640;
    canvas.height = v.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    setSelfie(canvas.toDataURL("image/jpeg", 0.85));
    stopCamera();
  }

  async function submit() {
    if (!selfie) {
      toast.error("Please capture a selfie first");
      return;
    }
    setSubmitting(true);
    try {
      const fp = await getFingerprint();
      await api.post("/me/devices/request-unlock", {
        device_id: getDeviceId(),
        device_fp: fp,
        device_user_agent: navigator.userAgent,
        selfie_b64: selfie,
      });
      toast.success("Unlock request sent to admin. You'll be notified when approved.");
      deviceStatusStore.set("pending");
      setDeviceStatus("pending");
      setDialogOpen(false);
      setSelfie(null);
      stopCamera();
    } catch (e) {
      toast.error(errorMsg(e, "Failed to submit unlock request"));
    } finally {
      setSubmitting(false);
    }
  }

  function openDialog() {
    setDialogOpen(true);
    setTimeout(() => startCamera(), 100);
  }

  if (!isField) return null;
  if (deviceStatus === "approved" || deviceStatus === "not_required" || !deviceStatus) {
    return null;
  }
  if (user?.role === "pmc_supervisor" && window.location.pathname.includes("/pmc/attendance") && sessionStorage.getItem("pmc_remote_mode") === "1") {
    return null;
  }

  const isRevoked = deviceStatus === "revoked";
  const tone = isRevoked
    ? "bg-rose-50 text-rose-900 ring-rose-200"
    : "bg-amber-50 text-amber-900 ring-amber-200";
  const icon = isRevoked ? "Revoked" : "Pending approval";

  return (
    <>
      <div
        className={`flex flex-wrap items-center gap-3 rounded-md px-3 py-2 text-sm ring-1 ${tone}`}
        data-testid="device-status-banner"
      >
        <ShieldAlert className="h-4 w-4 flex-none" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold">
            This device is {isRevoked ? "revoked" : "not approved"}.
          </div>
          <div className="text-[12px] opacity-90">
            Please request the admin for approval to capture attendance from this device.
          </div>
        </div>
        <Button
          size="sm"
          variant={isRevoked ? "destructive" : "default"}
          onClick={openDialog}
          data-testid="open-device-unlock-dialog"
        >
          <Camera className="mr-1.5 h-4 w-4" />
          Request approval
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) stopCamera(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request device approval</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Please capture a clear selfie so the admin can verify it's you before approving this device.
            </p>
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-md bg-slate-900">
              {selfie ? (
                <img src={selfie} alt="captured" className="h-full w-full object-cover" />
              ) : (
                <video ref={videoRef} className="h-full w-full object-cover" muted playsInline data-testid="device-unlock-video" />
              )}
            </div>
            <div className="flex justify-center gap-2">
              {selfie ? (
                <Button variant="outline" onClick={() => { setSelfie(null); startCamera(); }} data-testid="device-unlock-retake">
                  <RotateCw className="mr-1.5 h-4 w-4" /> Retake
                </Button>
              ) : (
                <Button onClick={capture} data-testid="device-unlock-capture">
                  <Camera className="mr-1.5 h-4 w-4" /> Capture
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDialogOpen(false); stopCamera(); }} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!selfie || submitting} data-testid="device-unlock-submit">
              {submitting ? "Submitting…" : "Send to admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
